import {useSelector, useField as useTanStackField} from '@tanstack/react-form';
import * as React from 'react';

import type {FormInstance} from './context';
import {useFormContext} from './context';
import type {
  FieldApi,
  FieldOptions,
  FieldState,
  Validate,
  ValidateOn,
} from './types';

/**
 * Every field registers its `validate` in the `onChange` slot, whatever its
 * `validateOn` — `validateOn` decides when we ask that slot to run, not which
 * slot it lives in. Keeping to one slot means a field has one error at a time,
 * so an error can never be left behind by a slot that didn't re-run, and
 * `setError` from elsewhere writes where the field's own validation will
 * overwrite it. Submitting runs every slot, so `onChange` is always covered.
 */
const ERROR_SLOT = 'onChange';

const triggersFor = (validateOn: ValidateOn) => ({
  onBlur: validateOn.includes('blur'),
  onChange: validateOn.includes('change'),
});

/**
 * `''` means "no value" unless the form opted into empty strings, so a field
 * the user cleared submits nothing rather than a blank.
 */
const normalizeValue = (value: unknown, allowEmptyStrings: boolean): unknown =>
  value === '' && !allowEmptyStrings ? undefined : value;

/**
 * A falsy error means "no error". Left in the error map it would keep the field
 * invalid and block submitting, so it's dropped instead.
 */
const setFieldError = (form: FormInstance, name: string, error: unknown) => {
  form.setFieldMeta(name, meta => ({
    ...meta,
    errorMap: {...meta.errorMap, [ERROR_SLOT]: error || undefined},
  }));
};

/**
 * Subscribes to one field of the surrounding `<Form>`, returning its current
 * value and error alongside the handle used to write to it.
 *
 * Field components pass their own props straight through, so field options
 * travel with the rest of the component's props:
 *
 * ```tsx
 * const MyInput = (props: FieldProps<{label: string}>) => {
 *   const {fieldState, fieldApi} = useField(props);
 *   // ...
 * };
 * ```
 */
export const useField = <TProps extends FieldOptions>(
  props: TProps,
): {fieldApi: FieldApi; fieldState: FieldState} => {
  const {allowEmptyStrings, form} = useFormContext();
  const {initialValue, keepState, name, validate, validateOn = 'blur'} = props;

  // `validate` is usually a fresh closure every render, so it's read through a
  // ref: the validator handed to the form stays put while still running the
  // current one.
  const validateRef = React.useRef<Validate | undefined>(validate);
  validateRef.current = validate;
  const runValidate = React.useCallback(
    ({value}: {value: unknown}) =>
      validateRef.current?.(value, form.state.values),
    [form],
  );

  const field = useTanStackField({
    // A field that unmounts without `keepState` takes its value with it, so it
    // needs the form's initial value to come back to on remount — otherwise
    // remounting (which React does on its own in development) would leave the
    // field blank.
    defaultValue: initialValue ?? form.options.defaultValues?.[name],
    form,
    name,
    validators: validate ? {[ERROR_SLOT]: runValidate} : {},
  });

  const fieldRef = React.useRef(field);
  fieldRef.current = field;

  const keepStateRef = React.useRef(keepState);
  keepStateRef.current = keepState;
  React.useEffect(
    () => () => {
      if (!keepStateRef.current) {
        form.deleteField(name);
      }
    },
    [form, name],
  );

  const {onBlur: validatesOnBlur, onChange: validatesOnChange} =
    triggersFor(validateOn);

  const fieldApi = React.useMemo(
    (): FieldApi => ({
      setError: error => {
        setFieldError(form, name, error);
      },
      setTouched: touched => {
        if (!touched) {
          fieldRef.current.setMeta(meta => ({...meta, isTouched: false}));
          return;
        }
        fieldRef.current.handleBlur();
        // `handleBlur` runs the blur slot, which is never where the validator
        // lives, so a field that validates on blur has to run its own slot.
        if (validatesOnBlur) {
          void fieldRef.current.validate('change');
        }
      },
      setValue: value => {
        fieldRef.current.setValue(normalizeValue(value, allowEmptyStrings), {
          dontValidate: !validatesOnChange,
        });
      },
    }),
    [allowEmptyStrings, form, name, validatesOnBlur, validatesOnChange],
  );

  const error = field.state.meta.errors[0] as unknown;
  const value = field.state.value as unknown;
  const fieldState = React.useMemo(() => ({error, value}), [error, value]);

  return {fieldApi, fieldState};
};

/**
 * Reads another field's state by name, for components that render off a field
 * they don't own.
 */
export const useFieldState = (name: string): FieldState => {
  const {form} = useFormContext();
  const error = useSelector(
    form.store,
    state => state.fieldMeta[name]?.errors[0] as unknown,
  );
  const value = useSelector(form.store, state => state.values[name] as unknown);
  return React.useMemo(() => ({error, value}), [error, value]);
};

/**
 * Writes to another field by name. Unlike {@link useFieldState} this doesn't
 * subscribe, so the caller won't re-render when that field changes.
 */
export const useFieldApi = (name: string): FieldApi => {
  const {allowEmptyStrings, form} = useFormContext();
  return React.useMemo(
    (): FieldApi => ({
      setError: error => {
        setFieldError(form, name, error);
      },
      setTouched: touched => {
        form.setFieldMeta(name, meta => ({...meta, isTouched: touched}));
      },
      setValue: value => {
        form.setFieldValue(name, normalizeValue(value, allowEmptyStrings));
      },
    }),
    [allowEmptyStrings, form, name],
  );
};
